import { ThemeEvents } from '@theme/events';

/**
 * Repeats the existing product button in an editorial chapter. The native
 * product form owns variants, quantity, validation, submission and cart events.
 */
class ProductFormCta extends HTMLElement {
  #form;
  #button;
  #error;
  #observer = new MutationObserver(() => this.#sync());
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    this.#button = this.querySelector('button');
    this.#error = this.querySelector('[data-native-error]');
    const { signal } = this.#controller;
    this.addEventListener('click', this.#activate, { signal });
    document.addEventListener(ThemeEvents.variantUpdate, this.#refresh, { signal });
    document.addEventListener('shopify:section:load', this.#refresh, { signal });
    this.#bind();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    this.#controller?.abort();
  }

  #refresh = () => queueMicrotask(() => {
    if (this.isConnected) this.#bind();
  });

  #bind() {
    this.#observer.disconnect();
    const test = window.SofthourPriceAB;
    if (test && Object.values(test.pids).includes(String(this.dataset.productId))) {
      this.dataset.productId = test.renderedProductId;
    }
    this.#form = document.querySelector(
      `main product-form-component[data-product-id="${CSS.escape(this.dataset.productId || '')}"]`
    );
    if (this.#form) {
      this.#observer.observe(this.#form, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-busy', 'data-added', 'class', 'hidden'],
      });
    }
    this.#sync();
  }

  #sourceButton() {
    return this.#form?.querySelector('add-to-cart-component [ref="addToCartButton"]');
  }

  #sync() {
    const source = this.#sourceButton();
    if (!source || !this.#button) {
      this.hidden = true;
      return;
    }
    this.#button.className = source.className;
    if (this.#button.innerHTML !== source.innerHTML) {
      this.#button.replaceChildren(...Array.from(source.childNodes, (node) => node.cloneNode(true)));
    }
    const busy = this.#form.getAttribute('aria-busy') === 'true';
    this.#button.disabled = source.disabled || busy;
    this.#button.setAttribute('aria-busy', String(busy));
    this.#button.dataset.added = source.dataset.added || 'false';
    this.#syncError();
    this.hidden = false;
  }

  #syncError() {
    if (!this.#error) return;
    const sourceError = this.#form.querySelector('[ref="addToCartTextError"]');
    const message = sourceError && !sourceError.hidden && !sourceError.classList.contains('hidden')
      ? sourceError.textContent.trim().replace(/\s+/g, ' ')
      : '';
    // The native assertive live region already announces this error once.
    // Repeat it visually here and associate it with this button, without a
    // second live region announcing the same failure again.
    if (this.#error.textContent !== message) this.#error.textContent = message;
    this.#error.hidden = !message;
    if (message) this.#button.setAttribute('aria-describedby', this.#error.id);
    else this.#button.removeAttribute('aria-describedby');
  }

  #activate = (event) => {
    if (!event.target.closest('button') || this.#button?.disabled) return;
    const source = this.#sourceButton();
    if (!source || source.disabled || this.#form.getAttribute('aria-busy') === 'true') return;
    source.click();
  };
}

if (!customElements.get('product-form-cta')) {
  customElements.define('product-form-cta', ProductFormCta);
}
